import { useEffect, useMemo, useRef, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { DEFAULT_THUMBNAIL, generateScreenshotUrl } from '../../utils/screenshot';
import './Header.css';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

export const Header = ({
  siteUrl = 'coca-cola.com',
  reportDate = 'March 26, 2025 13:30:54',
  score = 50,
  passed = 7,
  toImprove = 3,
  errors = 8,
  screenshotUrl,
}) => {
  
  const thumIoScreenshotUrl = useMemo(() => generateScreenshotUrl(siteUrl), [siteUrl]);
  const apiScreenshotUrl = (screenshotUrl || '').trim();

  // Start with API screenshot if provided; otherwise start directly with thum.io.
  const [previewScreenshotUrl, setPreviewScreenshotUrl] = useState(
    apiScreenshotUrl || thumIoScreenshotUrl
  );

  // Track whether we've already attempted the thum.io fallback for the current props.
  const hasTriedThumIoFallbackRef = useRef(!apiScreenshotUrl);

  useEffect(() => {
    hasTriedThumIoFallbackRef.current = !apiScreenshotUrl;
    setPreviewScreenshotUrl(apiScreenshotUrl || thumIoScreenshotUrl);
  }, [apiScreenshotUrl, thumIoScreenshotUrl]);

  const handleScreenshotError = () => {
    // If the API screenshot failed, fall back to thum.io (once).
    if (!hasTriedThumIoFallbackRef.current) {
      hasTriedThumIoFallbackRef.current = true;
      setPreviewScreenshotUrl(thumIoScreenshotUrl);
      return;
    }

    // If thum.io also failed, fall back to the default thumbnail.
    if (previewScreenshotUrl !== DEFAULT_THUMBNAIL) {
      setPreviewScreenshotUrl(DEFAULT_THUMBNAIL);
    }
  };
  
  // Chart.js configuration for score gauge (half circle with gradient segments)
  const baseSegments = [
    { from: 0, to: 50, color: '#F04D50' },    
    { from: 51, to: 70, color: '#FFA232' },  
    { from: 71, to: 85, color: '#FFDF6F' },
    { from: 86, to: 100, color: '#41D48C' },
  ];

  const overlaySegments = [
    { from: 0, to: Math.max(score, 0), color: 'rgba(0,0,0,0)' },
    { from: Math.max(score, 0), to: 100, color: '#FFFFFF' },
  ];
  
  const chartData = {
    datasets: [
      {
        data: baseSegments.map(segment => segment.to - segment.from),
        backgroundColor: baseSegments.map(segment => segment.color),
        borderWidth: 0,
        borderColor: 'transparent',
        circumference: 180,
        rotation: 270,
        cutout: '68%',
      },
      {
        data: overlaySegments.map(segment => segment.to - segment.from),
        backgroundColor: overlaySegments.map(segment => segment.color),
        borderWidth: 0,
        borderColor: 'transparent',
        circumference: 180,
        rotation: 270,
        cutout: '68%',
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
    },
  };

  const handlePrintReport = () => {
    if (typeof document === 'undefined' || typeof window === 'undefined') {
      return;
    }

    const body = document.body;
    body.classList.add('seo-checker-print');

    const cleanupPrintMode = () => {
      body.classList.remove('seo-checker-print');
      window.removeEventListener('afterprint', cleanupPrintMode);
      if (mediaQueryList && typeof mediaQueryList.removeEventListener === 'function') {
        mediaQueryList.removeEventListener('change', handlePrintChange);
      } else if (mediaQueryList && typeof mediaQueryList.removeListener === 'function') {
        mediaQueryList.removeListener(handlePrintChange);
      }
    };

    const handlePrintChange = (event) => {
      if (!event.matches) {
        cleanupPrintMode();
      }
    };

    window.addEventListener('afterprint', cleanupPrintMode);

    const mediaQueryList = window.matchMedia && window.matchMedia('print');
    if (mediaQueryList && typeof mediaQueryList.addEventListener === 'function') {
      mediaQueryList.addEventListener('change', handlePrintChange);
    } else if (mediaQueryList && typeof mediaQueryList.addListener === 'function') {
      mediaQueryList.addListener(handlePrintChange);
    }

    window.print();
  };

  const handleViewResults = () => {
    // Scroll to content section (same behavior as Content menu item)
    const contentSection = document.getElementById('content');
    if (contentSection) {
      const offset = 120;
      const targetPosition = contentSection.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: targetPosition, behavior: 'smooth' });
    }
  };

  return (
    <div className="seo-checker-header">
      {/* Header Row */}
      <div className="seo-checker-header__row">
        <div className="seo-checker-header__row-content">
          <div className="seo-checker-header__row-left">
            <h1 className="seo-checker-header__title">
              <span>Your SEO Results</span>
              <button 
                className="seo-checker-header__print-btn seo-checker-header__print-btn--mobile"
                onClick={handlePrintReport}
                type="button"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14 6H2C1.44772 6 1 6.44772 1 7V11C1 11.5523 1.44772 12 2 12H3V14C3 14.5523 3.44772 15 4 15H12C12.5523 15 13 14.5523 13 14V12H14C14.5523 12 15 11.5523 15 11V7C15 6.44772 14.5523 6 14 6Z" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M11 1H5C4.44772 1 4 1.44772 4 2V6H12V2C12 1.44772 11.5523 1 11 1Z" stroke="currentColor" strokeWidth="1.5"/>
                </svg>
                Print Report
              </button>
            </h1>
            <div className="seo-checker-header__meta">
              <span className="seo-checker-header__url">{siteUrl}</span> &nbsp; | &nbsp;
              <span className="seo-checker-header__date">Date Crawled: {reportDate}</span>
            </div>
          </div>
          <button 
            className="seo-checker-header__print-btn seo-checker-header__print-btn--desktop"
            onClick={handlePrintReport}
            type="button"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14 6H2C1.44772 6 1 6.44772 1 7V11C1 11.5523 1.44772 12 2 12H3V14C3 14.5523 3.44772 15 4 15H12C12.5523 15 13 14.5523 13 14V12H14C14.5523 12 15 11.5523 15 11V7C15 6.44772 14.5523 6 14 6Z" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M11 1H5C4.44772 1 4 1.44772 4 2V6H12V2C12 1.44772 11.5523 1 11 1Z" stroke="currentColor" strokeWidth="1.5"/>
            </svg>
            Print Report
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="seo-checker-header__content">
        <div className="seo-checker-header__score-section">
          {/* Score Gauge */}
          <div className="seo-checker-header__gauge">
            <div className="seo-checker-header__gauge-chart">
              <Doughnut data={chartData} options={chartOptions} />
            </div>
            <div className="seo-checker-header__gauge-text">
              <div className="seo-checker-header__gauge-label">Score</div>
              <div className="seo-checker-header__gauge-value">{score}</div>
            </div>
            {/* Score indicator circle */}
            <div 
              className="seo-checker-header__gauge-indicator"
              style={{ '--indicator-angle': `${(score / 100) * 180 - 90}deg` }}
            />
          </div>
          
          {/* Stats Row */}
          <div className="seo-checker-header__stats">
            <div className="seo-checker-header__stat">
              <div className="seo-checker-header__stat-badge seo-checker-header__stat-badge--passed">
                {passed}
              </div>
              <span className="seo-checker-header__stat-label">Passed</span>
            </div>
            <div className="seo-checker-header__stat">
              <div className="seo-checker-header__stat-badge seo-checker-header__stat-badge--improve">
                {toImprove}
              </div>
              <span className="seo-checker-header__stat-label">To Improve</span>
            </div>
            <div className="seo-checker-header__stat">
              <div className="seo-checker-header__stat-badge seo-checker-header__stat-badge--errors">
                {errors}
              </div>
              <span className="seo-checker-header__stat-label">Errors</span>
            </div>
          </div>
        </div>

        {/* Screenshot Preview */}
        <div className="seo-checker-header__preview">
          <img 
            src={previewScreenshotUrl} 
            alt={`Screenshot of ${siteUrl}`}
            className="seo-checker-header__preview-image"
            onError={handleScreenshotError}
          />
        </div>
      </div>

      {/* CTA Button */}
      <div className="seo-checker-header__cta-wrapper">
        <button 
          className="seo-checker-header__cta-btn"
          onClick={handleViewResults}
          type="button"
        >
          <span>View My Results</span>
          <svg width="19" height="16" viewBox="0 0 19 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9.37 1L9.37 15M9.37 15L2 7.63M9.37 15L16.75 7.63" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>

      {/* Bottom Text */}
      <div className="seo-checker-header__footer">
        <p className="seo-checker-header__footer-text">
          Your SEO audit is a free, <strong>no-strings-attached report</strong> to help you understand the overall health of your site at a glance.
          <br />
          Use this report to address quick wins, or deeper technical SEO issues on your site, to improve your rankings today!
        </p>
      </div>
    </div>
  );
};

